import moment from 'moment';
import { DATE_FORMAT, WAIT_TIME_URGENCY_THRESHOLD } from '../constants';
import { dedupe, dedupeForProperty, groupBy } from '.';
import { translate } from 'react-i18nify';

export const byLatestObservationDate = (a: Observation, b: Observation) => {
  if (a.authored > b.authored) {
    return -1;
  } else if (a.authored < b.authored) {
    return 1;
  }

  return 0;
};

export const byOldestObservationDate = (a: Observation, b: Observation) => {
  if (a.authored > b.authored) {
    return 1;
  } else if (a.authored < b.authored) {
    return -1;
  }

  return 0;
};

export function getCombinedStatus(assessments: Assessment[]): AssessmentStatus {
  if (!assessments.length) {
    return 'UNKNOWN';
  }

  if (assessments.some((a) => a.status === 'READY_FOR_VERIFICATION')) {
    return 'READY_FOR_VERIFICATION';
  }

  if (assessments.every((a) => a.status === 'REJECTED')) {
    return 'REJECTED';
  }

  if (assessments.every((a) => a.status === 'SIGNED')) {
    return 'SIGNED';
  }

  return 'UNKNOWN';
}

export const blacklistedEventTypes = [
  'checkup',
  'threeMonthCheckup',
  'yearlyCheckup',
  'reminder',
  'nurseReminder',
  'nurseCheckup',
  'patientNotReady',
  'onboardingComplete',
  'followupMissing',
  'memberMissing',
  'titrationIncomplete',
  'profileUpdate',
  'referralFailed',
  'slowOnboarding'
];

export function getObservationValue(assessment: Assessment, codeSystem: CodeSystem, code: string) {
  return getObservation(assessment.investigation?.observations || [], codeSystem, code, 'latest');
}

export function getObservation(
  observations: Observation[],
  codeSystem: CodeSystem,
  code: string,
  order: 'latest' | 'oldest' = 'latest'
) {
  if (!observations.length) {
    return;
  }

  return observations
    .sort(order === 'latest' ? byLatestObservationDate : byOldestObservationDate)
    .find((o) => o.requirement.requestedData.codeSystem === codeSystem && o.requirement.requestedData.code === code)
    ?.value;
}

export function getObservationDate(
  observations: Observation[],
  codeSystem: CodeSystem,
  code: string,
  order: 'latest' | 'oldest' = 'latest'
) {
  if (!observations.length) {
    return;
  }

  return observations
    .sort(order === 'latest' ? byLatestObservationDate : byOldestObservationDate)
    .find((o) => o.requirement.requestedData.codeSystem === codeSystem && o.requirement.requestedData.code === code)
    ?.authored;
}

export function conclusionsAreEqual(conclusionA: ClinicalConclusion, conclusionB: ClinicalConclusion) {
  return (
    conclusionA.codeSystem === conclusionB.codeSystem &&
    conclusionA.code === conclusionB.code &&
    conclusionA.interpretation === conclusionB.interpretation
  );
}

export function codeablesAreEqual(codeableA: Codeable, codeableB: Codeable) {
  return codeableA.codeSystem === codeableB.codeSystem && codeableA.code === codeableB.code;
}

export function getSelectedConclusion(assessment: Assessment): ClinicalConclusion | undefined {
  if (!assessment.clinicalConclusion) {
    return;
  }

  return assessment.possibleConclusions.find(
    (c) =>
      c.code === assessment.clinicalConclusion?.code &&
      c.codeSystem === assessment.clinicalConclusion?.codeSystem &&
      c.interpretation === assessment.clinicalConclusion?.interpretation &&
      (assessment.clinicalConclusion.category ? c.category === assessment.clinicalConclusion.category : true)
  );
}

export function getDefaultSelectedConclusionListItem(
  conclusionListItems: ListboxItem[],
  assessment: Assessment
): ListboxItem | undefined {
  return conclusionListItems.find(
    (c) =>
      c.value === getSelectedConclusion(assessment)?.id &&
      (assessment?.clinicalConclusion?.category ? c.category === assessment?.clinicalConclusion?.category : true)
  );
}

export function getPossibleConclusionsListItems(
  assessment: Assessment,
  stringProvider: (conclusion: ClinicalConclusion) => string
): ListboxItem[] {
  const noOptionSelected: ListboxItem = { id: -1, value: -1, label: 'Välj slutsats', isCategory: false };
  const requiresGrouping = assessment.possibleConclusions.some((c) => c.category);

  return [
    noOptionSelected,
    ...(requiresGrouping
      ? getGroupedConclusionListItems(assessment, stringProvider)
      : getConclusionListItems(assessment, stringProvider))
  ];
}

function getGroupedConclusionListItems(
  assessment: Assessment,
  stringProvider: (conclusion: ClinicalConclusion) => string
): ListboxItem[] {
  const items: ListboxItem[] = [];
  const groupedConclusions = groupBy(assessment.possibleConclusions, 'category');

  for (const group in groupedConclusions) {
    items.push({
      id: group,
      label: group,
      value: group,
      isCategory: true
    });

    items.push(
      ...groupedConclusions[group].map((c: ClinicalConclusion) => ({
        id: `${group}-${c.id}`,
        value: c.id,
        label: stringProvider(c),
        isChild: true,
        category: group
      }))
    );
  }

  return items;
}

function getConclusionListItems(
  assessment: Assessment,
  stringProvider: (conclusion: ClinicalConclusion) => string
): ListboxItem[] {
  return assessment.possibleConclusions.map((c) => ({
    id: c.id,
    value: c.id,
    label: stringProvider(c)
  }));
}

export function getAuthor(chatMessage: ChatMessage, authors: AuthorV2[] = []) {
  const author = authors.find((author) => author.userId === chatMessage.author);

  return author;
}

export function convertAuthorType(authors: Author[] = []): AuthorV2[] {
  return authors.map((author) => ({ userId: author.id, role: author.type, ...author }));
}

export function measurementIsNotInObervations(observations: Observation[]) {
  return (observationSubmission: ObservationSubmission) =>
    !observations.some((existing) => {
      const measurementId = existing.supportingInfo?.find((info) => info.infoType === 'IDENTIFIER')?.referenceId;
      const observationMeasurementId = observationSubmission.supportingInfo?.find(
        (info) => info.infoType === 'IDENTIFIER'
      )?.referenceId;

      if (measurementId && observationMeasurementId) {
        return measurementId === observationMeasurementId;
      }

      return (
        moment(existing.authored).isSame(observationSubmission.authored) &&
        existing.components?.every((part) =>
          observationSubmission.components?.some((comparison) => part.value.value === comparison.value.value)
        )
      );
    });
}

type WaitTimeUrgency = 'high' | 'low' | 'none';

export function getWaitTimeUrgency(waitTime: number, role?: CaregiverRole): WaitTimeUrgency {
  if (!role) {
    return 'none';
  }

  if (waitTime >= WAIT_TIME_URGENCY_THRESHOLD[role].HIGH) {
    return 'high';
  } else if (waitTime >= WAIT_TIME_URGENCY_THRESHOLD[role].LOW) {
    return 'low';
  } else {
    return 'none';
  }
}

export function getFullName(user: { givenName?: string; familyName?: string }, fallback = '') {
  if (!user || !(user.givenName || user.familyName)) {
    return fallback;
  }

  return `${user.givenName}${user.familyName ? ` ${user.familyName}` : ''}`;
}

export function getTimeUntilDate(date: string, unit: moment.unitOfTime.Diff = 'days') {
  const now = moment();
  const compareDate = moment(date, DATE_FORMAT);

  return compareDate.diff(now, unit as moment.unitOfTime.Diff);
}

export function findNextWorkItem(
  currentCaregiverGuid?: string,
  activities?: LightWeightWorkItem[],
  excludedItemIds?: string[]
) {
  if (!currentCaregiverGuid || !activities || !activities.length) {
    return;
  }

  const relevantActivities = activities.filter(
    (a) => a.caregiverAssignedToItem?.guid === currentCaregiverGuid && !excludedItemIds?.includes(a.id)
  );

  const appointments = relevantActivities.filter((a) => a.type === 'APPOINTMENT' && a.state !== 'SIGNED');

  if (appointments.length) {
    return appointments[0];
  }

  const errands = relevantActivities.filter((a) => a.type === 'ERRAND' && a.state !== 'SIGNED');

  if (errands.length) {
    return errands[0];
  }

  const monitorings = relevantActivities.filter((a) => a.type === 'MONITORING' && a.state !== 'SIGNED');

  if (monitorings.length) {
    return monitorings[0];
  }

  const programs = relevantActivities.filter((a) => a.type === 'PROGRAM' && a.state !== 'SIGNED');

  if (programs.length) {
    return programs[0];
  }
}

export function capitalize(str?: string) {
  if (!str) {
    return '';
  }

  return str[0].toUpperCase() + str.slice(1);
}

export function getAllMonthsInRange(from: string, to: string) {
  const format = 'YYYY-MM-01';
  const months: string[] = [];
  const momentStart = moment(from, DATE_FORMAT);
  const momentTo = moment(to, DATE_FORMAT);

  while (momentStart.isSameOrAfter(momentTo, 'month')) {
    months.push(momentStart.format(format));
    momentStart.subtract(1, 'month');
  }

  return months;
}

type GroupedTimelineItems = Record<string, TreatmentTimelineItem[]>;

export function groupTreatmentItemsByMonth(
  treatmentItems: TreatmentTimelineItem[],
  newestDate: string,
  oldestDate: string
) {
  const groupedByMonth: GroupedTimelineItems = {};

  if (!treatmentItems.length || !newestDate || !oldestDate) {
    return groupedByMonth;
  }

  const monthsInRange = getAllMonthsInRange(newestDate, oldestDate);

  monthsInRange.forEach((month) => {
    groupedByMonth[month] = treatmentItems.filter((item) => {
      const relevantDate = item.signedDate || item.startDate;

      const current = moment(month);
      return moment(relevantDate, DATE_FORMAT).isSame(current, 'month');
    });
  });

  return groupedByMonth;
}

export function isFollowUpSelectionComplete(selectedFollowUpActivity?: Pick<SuggestedFollowUp, 'actions'>) {
  const noOptionSelected = selectedFollowUpActivity?.actions?.some((a) => a.options?.length && !a.selectedOption);
  const noSelectedDate = selectedFollowUpActivity?.actions?.some((a) => a.canChangeDate && !a.plannedStartDate);

  return selectedFollowUpActivity && !noOptionSelected && !noSelectedDate;
}

export async function getFriendlyErrorMessageFromResponse(res: Response): Promise<string> {
  if (res.ok) {
    return '';
  }

  const responseContent = await res.json();

  if (!responseContent?.details?.friendlyMessage) {
    return '';
  }

  return responseContent.details.friendlyMessage;
}

export const addEmptyValuesForMissingAnalyses = (labResults: LabResultOG[]) => {
  const allTimestamps: string[] = [];

  if (!labResults || !labResults.length) {
    return [];
  }

  labResults.forEach((result) => {
    result.values?.forEach((value) => {
      if (!allTimestamps.includes(value.timestamp)) {
        allTimestamps.push(value.timestamp);
      }
    });
  });

  labResults.forEach((result) => {
    const values = [...result.values];
    const valueDates = values?.map((v) => v.timestamp);
    const missingValuesForDates = allTimestamps.filter((t) => !valueDates.includes(t));

    if (missingValuesForDates.length) {
      const newValues = [
        ...dedupeForProperty(result.values, 'timestamp'),
        ...missingValuesForDates.map((timestamp) => ({ value: undefined, timestamp }))
      ].sort(sortByTimestamp);

      result.values = newValues;
    }
  });

  return labResults;
};

const sortByTimestamp = (a, b) => {
  if (a.timestamp === b.timestamp) {
    return 0;
  }

  if (a.timestamp > b.timestamp) {
    return -1;
  }

  return 1;
};

export function getAssessmentQuestionTooltipContent(
  observation: Observation,
  supportingInfo?: SupportingInfo[] | SupportingInfo,
  readableValue?: string
) {
  // Normalize supportingInfo to an array
  const safeSupportingInfo = Array.isArray(supportingInfo) ? supportingInfo : supportingInfo ? [supportingInfo] : [];

  const infoSections = safeSupportingInfo
    .map((info) => {
      const questionAndOptions = info.title || '';
      const description = info.description || readableValue || '-';

      const answerOptions =
        questionAndOptions && questionAndOptions.split('\n').length > 1
          ? questionAndOptions
              .split('\n')[1]
              .replace('Svarsalternativ: ', '')
              .split(/,\s+(?=[A-Z1-9])/)
          : [];

      return `
        ${questionAndOptions ? `<strong>${questionAndOptions.split('\n')[0]}</strong><br />` : ''}
        ${observation.collectionMethod !== 'SYSTEM' ? `Tolkning: ${readableValue || '-'}<br />` : ''}
        Svar: ${description} (av ${translate(
          `journal.author_type.${observation.author.type.toLowerCase()}`
        ).toLowerCase()})<br /><br />
        ${
          answerOptions.length
            ? `
          Svarsalternativ:<br />
          ${answerOptions.map((option) => `• ${option.replace('.', '')}`).join('<br />')}
          <br /><br />
          `
            : ''
        }
      `;
    })
    .join('<hr style="margin:6px 0;" />');

  return `
    ${infoSections}
    ${observation.authored ? `Besvarades ${moment(observation.authored, DATE_FORMAT).format('YYYY-MM-DD')}` : ''}
  `;
}

export function getQuestionnaireTooltipContent(question: Question) {
  return `
  ${
    question.answerOptions?.length
      ? `
    Svarsalternativ:<br />
    ${question.answerOptions.map((option) => `• ${option.replace('.', '')}`).join('<br />')}
    <br /><br />
    `
      : ''
  }  
  Besvarades ${moment(question.updated, DATE_FORMAT).format('YYYY-MM-DD')}
  `;
}

const flowchartUrlMap: Record<string, string> = {
  BodyMeasurements: 'https://miro.com/app/board/uXjVKFyZeNQ=/?moveToWidget=3458764589595262000&cot=14',
  PhysicalActivity: 'https://miro.com/app/board/uXjVKFyZeNQ=/?moveToWidget=3458764589634302624&cot=14',
  PerceivedStressScale: 'https://miro.com/app/board/uXjVKFyZeNQ=/?moveToWidget=3458764589634302624&cot=14',
  SmokingHabits: 'https://miro.com/app/board/uXjVKFyZeNQ=/?moveToWidget=3458764589634302881&cot=14',
  SnuffConsumption: 'https://miro.com/app/board/uXjVKFyZeNQ=/?moveToWidget=3458764589634302881&cot=14',
  SaltIntake: 'https://miro.com/app/board/uXjVKFyZeNQ=/?moveToWidget=3458764589634745550&cot=14',
  LiquoriceConsumption: 'https://miro.com/app/board/uXjVKFyZeNQ=/?moveToWidget=3458764589634745550&cot=14',
  AlcoholDisorderIdentificationTest: 'https://miro.com/app/board/uXjVKFyZeNQ=/?moveToWidget=3458764589634302921&cot=14'
};

export function getFlowchartUrlForAssessment(assessment: Assessment) {
  return flowchartUrlMap[assessment.type];
}

export function getDiagnosisListForFinalNote(
  summary: EncounterSummary,
  selectedEligibleDiagnosisCode?: Codeable,
  excludedDiagnosisTypes?: DiagnosisType[]
): DiagnosisRequestModel[] {
  const diagnoses: DiagnosisRequestModel[] = [];

  if (summary.primaryDiagnosisAssessment?.clinicalConclusion?.interpretation === 'PRESENT') {
    diagnoses.push({
      type: 'PRIMARY',
      assessmentId: summary.primaryDiagnosisAssessment.id,
      code: {
        codeSystem: summary.primaryDiagnosisAssessment.clinicalConclusion.codeSystem,
        code: summary.primaryDiagnosisAssessment.clinicalConclusion.code
      }
    });
  } else if (
    selectedEligibleDiagnosisCode &&
    (!summary.primaryDiagnosisAssessment?.clinicalConclusion ||
      summary.primaryDiagnosisAssessment?.clinicalConclusion?.interpretation === 'ABSENT')
  ) {
    diagnoses.push({
      type: 'PRIMARY',
      code: selectedEligibleDiagnosisCode
    });
  }

  if (summary.supplementalDiagnosisAssessment?.clinicalConclusion?.interpretation === 'PRESENT') {
    diagnoses.push({
      type: 'SUPPLEMENTAL',
      assessmentId: summary.supplementalDiagnosisAssessment.id,
      code: {
        codeSystem: summary.supplementalDiagnosisAssessment.clinicalConclusion.codeSystem,
        code: summary.supplementalDiagnosisAssessment.clinicalConclusion.code
      }
    });
  }

  summary.procedureCodes?.forEach((c) => {
    diagnoses.push({
      type: 'PROCEDURAL',
      code: c
    });
  });

  return excludedDiagnosisTypes ? diagnoses.filter((d) => !excludedDiagnosisTypes.includes(d.type)) : diagnoses;
}

export const byLabel = (a: ListboxItem, b: ListboxItem) => {
  if (a.label > b.label) {
    return 1;
  }

  if (a.label < b.label) {
    return -1;
  }

  return 0;
};

export const byId = (a: { id: number }, b: { id: number }) => {
  if (a.id > b.id) {
    return 1;
  }

  if (a.id < b.id) {
    return -1;
  }

  return 0;
};

export const byModifiedDate = (a: { modified: string }, b: { modified: string }) => {
  const aModified = moment(a.modified, DATE_FORMAT);
  const bModified = moment(b.modified, DATE_FORMAT);

  if (aModified.isSameOrAfter(bModified)) {
    return -1;
  } else if (bModified.isAfter(aModified)) {
    return 1;
  }

  return 0;
};

export function getCategorizedSupportErrandListItems(
  errandTypes: SupportErrandType[],
  stringProvider: (type: string) => string,
  options?: { isUserPAL?: boolean; isUserPAS?: boolean }
) {
  const { isUserPAL = false, isUserPAS = false } = options || {};

  const toListBoxItemValue = (type: SupportErrandType, i: number): ListboxItem => ({
    id: type.type,
    label: stringProvider(type.type),
    value: type,
    isChild: true
  });

  const allErrandTypeItems: ListboxItem[] = [];

  // Errands with only PAL as recipient (hide if user is PAL for this patient)
  if (!isUserPAL) {
    const errandTypesToPAL = errandTypes
      .filter((e) => e.validRecipients.length === 1 && e.validRecipients[0] === 'PAL')
      .map(toListBoxItemValue)
      .sort(byLabel);

    if (errandTypesToPAL.length) {
      allErrandTypeItems.push({
        id: 100,
        label: 'Till PAL',
        isCategory: true
      });
      allErrandTypeItems.push(...errandTypesToPAL);
    }
  }

  // Errands with only PAS as recipient (hide if user is PAS for this patient)
  if (!isUserPAS) {
    const errandTypesToPAS = errandTypes
      .filter((e) => e.validRecipients.length === 1 && e.validRecipients[0] === 'PAS')
      .map(toListBoxItemValue)
      .sort(byLabel);

    if (errandTypesToPAS.length) {
      allErrandTypeItems.push({
        id: 150,
        label: 'Till PAS',
        isCategory: true
      });
      allErrandTypeItems.push(...errandTypesToPAS);
    }
  }

  // Errands with only COPS as recipient
  const errandTypesToSupport = errandTypes
    .filter((e) => e.validRecipients.length === 1 && e.validRecipients[0] === 'COPS')
    .map(toListBoxItemValue)
    .sort(byLabel);

  if (errandTypesToSupport.length) {
    allErrandTypeItems.push({
      id: 200,
      label: 'Till patientsupport',
      isCategory: true
    });
    allErrandTypeItems.push(...errandTypesToSupport);
  }

  // Errands with multiple possible recipients
  const multiRecipientErrands = errandTypes
    .filter((e) => e.validRecipients.length > 1)
    .map(toListBoxItemValue)
    .sort(byLabel);

  if (multiRecipientErrands.length) {
    allErrandTypeItems.push({
      id: 300,
      label: 'Välj mottagare',
      isCategory: true
    });
    allErrandTypeItems.push(...multiRecipientErrands);
  }

  allErrandTypeItems.unshift({ id: -1, label: 'Välj ärendetyp', value: null });

  return allErrandTypeItems;
}

export function getOrderedProgramBundle(bundle: ProgramBundleItem[]) {
  const bundleCopy = [...bundle];

  bundleCopy.forEach((item, i) => (item.bundleOrder = i + 1));

  return bundleCopy;
}

export const getAllLabTestTypesForPatient = (labResults: LabResult[]) => {
  let allTestNames = labResults
    .flatMap((l) =>
      l.samples.map((s) => ({
        testName: s.testName,
        testCode: s.testCode,
        unit: s.unit,
        goalValue: s.goalValue,
        severities: s.severities,
        sortOrder: s.sortOrder
      }))
    )
    .sort((a, b) => a.sortOrder - b.sortOrder);

  allTestNames = dedupeForProperty(allTestNames, 'testName');

  return allTestNames;
};

export function sortByProperty<T>(property: keyof T) {
  return (a: T, b: T) => {
    if (a[property] === b[property]) {
      return 0;
    }

    if (a[property] > b[property]) {
      return -1;
    }

    return 1;
  };
}

export function getAllAssessmentTypesInDelta(first: AssessmentDeltaWrapper, second: AssessmentDeltaWrapper) {
  const firstAssessments = first.assessments || {};
  const secondAssessments = second.assessments || {};
  const allAssessments = [...Object.keys(firstAssessments), ...Object.keys(secondAssessments)];

  return dedupe(allAssessments).sort();
}

export function getInboxSectionSearchParams(
  identifier: string,
  isViewingAllErrands: boolean,
  waitingTimeSortOrder: Record<string, SortOrder>,
  isViewingAllSharedQueueErrands: boolean,
  selectedCaregiver?: SystemUser,
  currentUserGuid?: string,
  isSharedQueue?: boolean,
  isViewingAllErrandsForRole?: boolean
) {
  const searchParams = new URLSearchParams();

  if (['signedAppointments', 'handledErrands', 'sharedQueueSigned'].includes(identifier)) {
    searchParams.append('sortDirection', 'DESC');
  } else {
    searchParams.append('sortDirection', waitingTimeSortOrder[identifier] || 'ASC');
  }

  if (selectedCaregiver) {
    searchParams.append('requestedCaregiverId', selectedCaregiver.guid);
  } else if (isViewingAllErrands) {
    searchParams.append('includeAll', 'true');
  }

  if (!selectedCaregiver && isViewingAllErrandsForRole && identifier === 'newErrands') {
    searchParams.append('viewAllErrandsForRole', 'true');
  }

  const appointmentRoleIdentifiers = [
    'newAppointments',
    'upcomingAppointments',
    'ongoingAppointments',
    'awaitingPatient',
    'signedAppointments'
  ];
  if (
    !selectedCaregiver &&
    isViewingAllErrandsForRole &&
    !isSharedQueue &&
    appointmentRoleIdentifiers.includes(identifier)
  ) {
    searchParams.append('viewAllAppointmentsForRole', 'true');
  }

  if (['newErrands', 'newAppointments', 'ongoingAppointments'].includes(identifier)) {
    searchParams.append('sortField', 'STATE_UPDATED');
  }

  if (isSharedQueue) {
    searchParams.append('includeAll', 'true');
  }

  if (!isViewingAllSharedQueueErrands && currentUserGuid) {
    searchParams.append('requestedCaregiverId', currentUserGuid);
    searchParams.delete('includeAll');
  }

  return searchParams;
}

export function getLabMetadata(appointment?: WorkflowAppointment): FollowUpLabMetadata | undefined {
  const selectedFollowUp = appointment?.followUpActivities?.find((f) => f.selected);
  const labAction = selectedFollowUp?.actions.find((a) => !!a.labMetadata);
  return labAction?.labMetadata;
}

export function arrayContentsMatch<T extends string | number>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;

  const countMap = new Map<T, number>();

  for (const item of a) {
    countMap.set(item, (countMap.get(item) ?? 0) + 1);
  }

  for (const item of b) {
    if (!countMap.has(item)) {
      return false;
    }

    const newCount = (countMap.get(item) ?? 0) - 1;

    if (newCount < 0) {
      return false;
    }

    countMap.set(item, newCount);
  }

  return true;
}

export function setSyntheticIds(items: any[]) {
  if (items.every((item) => typeof item.id !== 'undefined')) {
    return items;
  }

  const copy = [...items];
  copy.forEach((item, i) => (item.id = i));

  return copy;
}

export function getExpectedGoalValueAtTime(
  goal: ObesityTreatmentGoal,
  date: Date,
  latestWeight?: number,
  latestWeightDate?: Date
) {
  let expectedValue = 0;

  switch (goal.category) {
    case 'PERCENTAGE_WEIGHT_LOSS': {
      const startWeight = Number(goal.metrics['StartWeight'].value);
      const targetWeight = Number(goal.metrics['TargetWeight'].value);
      const startDate = moment(goal.metrics['StartDate'].value);
      const targetDate = moment(goal.metrics['TargetDate'].value);
      const rate = Math.abs(Number(goal.metrics['TargetPercentageWeightLoss'].value));

      if (moment(date).isBefore(startDate)) {
        expectedValue = startWeight;
        break;
      }

      if (moment(date).isSameOrAfter(targetDate)) {
        expectedValue = targetWeight;
        break;
      }

      const totalDuration = targetDate.valueOf() - startDate.valueOf();
      const elapsed = moment(date).valueOf() - startDate.valueOf();
      const progress = elapsed / totalDuration;
      const totalLoss = startWeight * (rate / 100);

      expectedValue = startWeight - totalLoss * progress;

      break;
    }
    case 'WEIGHT_LOSS': {
      if (latestWeight == null || !latestWeightDate) {
        break;
      }

      const targetWeight = Number(goal.metrics['TargetWeight'].value);
      const weeklyRate = Number(goal.metrics['TargetWeightLossPerWeek'].value);
      const latestDate = moment(latestWeightDate);

      if (moment(date).isBefore(latestDate)) {
        expectedValue = latestWeight;
        break;
      }

      if (goal.expectedTargetDate && moment(date).isSameOrAfter(moment(goal.expectedTargetDate))) {
        expectedValue = targetWeight;
        break;
      }

      const numDays = latestDate.diff(moment(date), 'days');
      expectedValue = latestWeight - (weeklyRate / 7) * numDays;

      break;
    }
  }

  return expectedValue;
}

export function getAppointmentsInDateRange(timeline: TreatmentTimelineItem[] = [], from: Date, to: Date) {
  return timeline.filter((item) => {
    if (!['APPOINTMENT', 'PROGRAM'].includes(item.type)) {
      return false;
    }

    if (['CANCELLED', 'PAUSED', 'RESCHEDULED'].includes(item.state)) {
      return false;
    }

    if (item.signedDate) {
      return moment(item.signedDate).isBetween(from, to);
    }

    if (item.startDate) {
      return moment(item.startDate).isBetween(from, to);
    }

    return false;
  });
}

/**
 * Groups observations by their correction relationships.
 * Observations without correctedById become map keys, and observations
 * with correctedById are grouped under their correcting observation.
 */
export function bundleLinkedObservations(observations: Observation[]): Map<Observation, Observation[]> {
  const map = new Map<Observation, Observation[]>();

  const keys = observations.filter((obs) => obs.correctedById == null);

  keys.forEach((key) => {
    const overriddenObservations = observations.filter((obs) => obs.correctedById === key.id);
    map.set(key, overriddenObservations);
  });

  return map;
}

export function isValidBodyMeasurement(num: number): boolean {
  return num > 30 && num < 350;
}

/**
 * Converts observation authored dates to Date objects for use with DatePicker excludeDates.
 */
export function getExcludedDatesFromObservations(observations: Observation[]): Date[] {
  return observations.map((o) => moment(o.authored, DATE_FORMAT).toDate());
}

export function canSendMessageInChatChannel(role: SystemRole, chatChannel?: ChatChannel) {
  if (!chatChannel) {
    return false;
  }

  const chatCapabilities: Record<SystemRole, ChatChannel[]> = {
    DOCTOR: ['DOCTOR'],
    NURSE: ['NURSE', 'MEDICAL_SUPPORT'],
    SUPPORT: [],
    TECH: []
  };

  return chatCapabilities[role].includes(chatChannel);
}

const AUTO_POPULATED_PARAMETER_TAG_PATTERN = /%([^%\s]+)%/g;

// URLs are percent-encoded, so an escape like `%20` looks exactly like the start
// of a `%TAG%` placeholder ("https://site.com?a=%20bla%20bla" would otherwise be
// read as the tag `%20bla%`). Anything inside a URL is therefore left alone: it
// is neither replaced nor warned about.
const URL_PATTERN = /\b(?:https?:\/\/|www\.)\S+/gi;

/** Applies `transform` to every part of `text` that falls outside a URL. */
function mapOutsideUrls(text: string, transform: (segment: string) => string): string {
  let result = '';
  let lastIndex = 0;

  URL_PATTERN.lastIndex = 0;
  let url = URL_PATTERN.exec(text);
  while (url !== null) {
    result += transform(text.slice(lastIndex, url.index)) + url[0];
    lastIndex = url.index + url[0].length;
    url = URL_PATTERN.exec(text);
  }

  return result + transform(text.slice(lastIndex));
}

const formatAutoPopulatedValue = ({ value, unit }: TemplateKeyValue): string => {
  const formatted = typeof value === 'number' ? value.toLocaleString('sv-SE') : value;
  return unit ? `${formatted} ${unit}` : formatted;
};

export function replaceAutoPopulatedParameters(text: string, parameters: TemplateKeyValue[] = []): string {
  if (!text || !parameters.length) {
    return text;
  }

  const lookup = new Map(parameters.map((parameter) => [parameter.key.toUpperCase(), parameter]));

  return mapOutsideUrls(text, (segment) =>
    segment.replace(AUTO_POPULATED_PARAMETER_TAG_PATTERN, (match, key: string) => {
      const parameter = lookup.get(key.toUpperCase());
      // Leave the tag untouched when the parameter is unknown or its data has
      // expired, so the caregiver is warned instead of pasting a stale value.
      if (parameter === undefined || parameter.status === 'EXPIRED') {
        return match;
      }
      return formatAutoPopulatedValue(parameter);
    })
  );
}

const TAG_BEFORE_CARET_PATTERN = /%[^%\s]+%$/;
const URL_START_PATTERN = /\b(?:https?:\/\/|www\.)/i;

/**
 * Replaces the `%TAG%` placeholder that ends right at `caret`, i.e. the one
 * whose closing `%` was just typed. Returns the new text and caret position, or
 * `null` when there is no such tag, it is unknown or expired, or it is part of a
 * URL.
 */
export function replaceAutoPopulatedParameterBeforeCaret(
  text: string,
  caret: number,
  parameters: TemplateKeyValue[] = []
): { text: string; caret: number } | null {
  const before = text.slice(0, caret);
  const tag = TAG_BEFORE_CARET_PATTERN.exec(before);

  if (!tag) {
    return null;
  }

  const word = before.slice(before.search(/\S*$/), tag.index);
  if (URL_START_PATTERN.test(word)) {
    return null;
  }

  const replaced = replaceAutoPopulatedParameters(tag[0], parameters);
  if (replaced === tag[0]) {
    return null;
  }

  return {
    text: before.slice(0, tag.index) + replaced + text.slice(caret),
    caret: tag.index + replaced.length
  };
}

/**
 * Returns the unique `%TAG%` placeholders still present in `text`, e.g. ['%BMI%'].
 * Percent-encoded escapes inside URLs are not placeholders and are ignored.
 */
export function getUnreplacedParameterTags(text = ''): string[] {
  const tags: string[] = [];
  mapOutsideUrls(text, (segment) => {
    tags.push(...(segment.match(AUTO_POPULATED_PARAMETER_TAG_PATTERN) ?? []));
    return segment;
  });
  return Array.from(new Set(tags));
}

/**
 * Warns (via a blocking alert) when `text` still contains `%TAG%` placeholders
 * that were never replaced with a value — e.g. an unrecognized tag, a valid tag
 * that was typed by hand instead of pasted, or a tag whose data has expired.
 * `parameters` is used to single out the expired ones in the message. Returns
 * `true` when the caller should abort the save/post so the caregiver can resolve
 * the tags first.
 */
export function warnOnUnreplacedParameterTags(text: string, parameters: TemplateKeyValue[] = []): boolean {
  const tags = getUnreplacedParameterTags(text);

  if (!tags.length) {
    return false;
  }

  const expiredKeys = new Set(
    parameters.filter((parameter) => parameter.status === 'EXPIRED').map((parameter) => parameter.key.toUpperCase())
  );
  const keyOf = (tag: string) => tag.replaceAll('%', '').toUpperCase();

  const expiredTags = tags.filter((tag) => expiredKeys.has(keyOf(tag)));
  const unknownTags = tags.filter((tag) => !expiredKeys.has(keyOf(tag)));

  const lines: string[] = [];
  if (unknownTags.length) {
    lines.push(`Taggar som det inte finns data i systemet för: ${unknownTags.join(', ')}.`);
  }
  if (expiredTags.length) {
    lines.push(`Taggar som inte fylldes i på grund av för gammal data: ${expiredTags.join(', ')}.`);
  }

  alert(`${lines.join('\n\n')}\n\nKontrollera taggarna manuellt innan du sparar/skickar.`);

  return true;
}

/**
 * Prepares text for saving/posting: expands any `%TAG%` placeholders that were
 * typed by hand (rather than pasted), then checks what is left. Returns the
 * expanded text and a `proceed` flag. When tags remain that could not be
 * expanded — unknown tags or expired data — a warning is shown and `proceed` is
 * false so the caller aborts and lets the caregiver resolve them first.
 */
export function resolveParameterTags(
  text: string,
  parameters: TemplateKeyValue[] = []
): { text: string; proceed: boolean } {
  const resolved = replaceAutoPopulatedParameters(text, parameters);
  const blocked = warnOnUnreplacedParameterTags(resolved, parameters);
  return { text: resolved, proceed: !blocked };
}
